import { useState } from "react";
import piAPI from "../../../../api/inventory/piApi";
import { showError, showSuccess } from "../../../../utils/toastUtils";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

export default function RecordPaymentModal({ isOpen, pi, onClose, onSuccess }) {
  const outstanding = pi ? parseFloat(pi.totalAmount - pi.amountPaid) : 0;

  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen || !pi) return null;

  const handlePayFull = () => setAmount(outstanding.toFixed(2));

  const handleSave = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Enter a valid amount");
      return;
    }
    if (amt > outstanding + 0.01) {
      setError(`Amount cannot exceed outstanding (₹${outstanding.toFixed(2)})`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await piAPI.recordPayment(pi.id, {
        amount: amt,
        paymentDate,
        method,
        reference: reference.trim() || null,
        notes: notes.trim() || null,
      });

      if (res.code === 200) {
        showSuccess("Payment recorded");
        // reset
        setAmount("");
        setReference("");
        setNotes("");
        onSuccess?.();
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to record payment";
      setError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title" id="delete-modalLabel">
              Record Payment
            </h4>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="bg-light rounded p-3 mb-3">
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Invoice:</span>
                <strong>{pi.invoiceNumber}</strong>
              </div>
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Total:</span>
                <span>₹{Number(pi.totalAmount).toLocaleString("en-IN")}</span>
              </div>
              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Already Paid:</span>
                <span>₹{Number(pi.amountPaid).toLocaleString("en-IN")}</span>
              </div>
              <div className="d-flex justify-content-between fw-bold">
                <span>Outstanding:</span>
                <span className="text-danger">
                  ₹{outstanding.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-7">
                <label className="form-label small">
                  Payment Amount <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={handlePayFull}
                  >
                    Full
                  </button>
                </div>
              </div>
              <div className="col-md-5">
                <label className="form-label small">Payment Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">Method</label>
                <select
                  className="form-select"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small">Reference</label>
                <input
                  type="text"
                  className="form-control"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Txn ID / Cheque no."
                />
              </div>
              <div className="col-12">
                <label className="form-label small">Notes</label>
                <input
                  type="text"
                  className="form-control"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="alert alert-danger mt-3 mb-0 py-2 small">
                {error}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-1" />
                  Record Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
